/**
 * Iter-372 (Frontend) — Timesheet Edit Modal
 *
 * Admin/super_admin/accounting/client can correct clock-in / clock-out
 * on a pending timesheet before payroll approval. Reason is required
 * (dropdown category + optional free-text note). Backend recalculates
 * hours + pay and writes a full audit-log row.
 *
 * Usage:
 *   <TimesheetEditModal
 *     open={editing}
 *     row={selectedRow}
 *     onClose={() => setEditing(false)}
 *     onSaved={(after) => refreshTimesheets()}
 *   />
 *
 * Backend contract:  PATCH /api/timesheet-edit/{booking_id}
 *   { clock_in_time?: iso, clock_out_time?: iso, reason: string }
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { AlertTriangle, Clock, Loader2, Save } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import { isoToTzInput, tzInputToIso, resolveEventTz, timezoneLabel } from '../utils/timeFormat';

const REASON_CATEGORIES = [
  { value: 'missed_clock_out',   label: 'Talent forgot to clock out' },
  { value: 'missed_clock_in',    label: 'Talent forgot to clock in' },
  { value: 'gps_error',          label: 'GPS / verification error' },
  { value: 'device_issue',       label: 'Device / app issue' },
  { value: 'manual_correction',  label: 'Manual correction (paper log)' },
  { value: 'client_request',     label: 'Client-requested adjustment' },
  { value: 'other',              label: 'Other' },
];

/** ISO datetime -> value string for <input type="datetime-local"> in the EVENT tz. */
function toLocalInput(iso, tz) {
  return isoToTzInput(iso, tz);
}

/** datetime-local value (EVENT tz wall clock) -> UTC ISO string. */
function fromLocalInput(v, tz) {
  return tzInputToIso(v, tz);
}

export default function TimesheetEditModal({ open, row, onClose, onSaved }) {
  const [clockIn, setClockIn] = useState('');
  const [clockOut, setClockOut] = useState('');
  const [category, setCategory] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const evtTz = resolveEventTz(row?.timezone, row?.state);

  useEffect(() => {
    if (open && row) {
      setClockIn(toLocalInput(row.clock_in_iso, evtTz));
      setClockOut(toLocalInput(row.clock_out_iso, evtTz));
      setCategory('');
      setNote('');
    }
  }, [open, row, evtTz]);

  const preview = useMemo(() => {
    const ci = clockIn ? new Date(fromLocalInput(clockIn, evtTz)) : null;
    const co = clockOut ? new Date(fromLocalInput(clockOut, evtTz)) : null;
    if (!ci || !co || Number.isNaN(ci.getTime()) || Number.isNaN(co.getTime())) return null;
    const brkMin = Number(row?.break_minutes || 0);
    const rawMin = Math.max(0, Math.floor((co - ci) / 60000) - brkMin);
    const hours  = Math.max(0, rawMin / 60);
    const reg    = Math.min(hours, 8);
    const ot     = Math.max(0, hours - 8);
    // PO-2026-06 FIX — staff payout preview uses the TALENT pay rate only,
    // never the client bill rate.
    const rate   = Number(row?.pay_rate ?? row?.talent_pay_rate ?? row?.effective_pay_rate ?? 0);
    const pay    = reg * rate + ot * rate * 1.5;
    return { hours, reg, ot, pay, valid: co > ci };
  }, [clockIn, clockOut, row, evtTz]);

  const anyChange =
    row &&
    (fromLocalInput(clockIn, evtTz)  !== (row.clock_in_iso  || null) ||
     fromLocalInput(clockOut, evtTz) !== (row.clock_out_iso || null));

  const submit = async () => {
    if (!row) return;
    if (!category) { toast.error('Pick a reason category.'); return; }
    if (!preview?.valid) { toast.error('Clock-out must be after clock-in.'); return; }
    const label = REASON_CATEGORIES.find(r => r.value === category)?.label || category;
    const reason = note.trim() ? `${label}: ${note.trim()}` : label;
    if (reason.length < 3) { toast.error('Reason too short.'); return; }
    setSaving(true);
    try {
      const body = {
        clock_in_time:  fromLocalInput(clockIn, evtTz),
        clock_out_time: fromLocalInput(clockOut, evtTz),
        reason,
      };
      const res = await api.patch(`/timesheet-edit/${row.booking_id}`, body);
      toast.success('Timesheet updated');
      onSaved?.(res.data);
      onClose?.();
    } catch (e) {
      const d = e?.response?.data?.detail;
      const msg = typeof d === 'object' ? (d.message || d.code) : (d || 'Failed to update timesheet');
      toast.error(msg);
    } finally { setSaving(false); }
  };

  if (!row) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (!v && !saving) && onClose?.()}>
      <DialogContent className="max-w-lg" data-testid="timesheet-edit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Clock className="w-4 h-4 text-cyan-600" />
            Edit clock times
          </DialogTitle>
          <p className="text-[11px] text-gray-500">
            {row.talent_name} · {row.event_name} · {row.shift_date}
          </p>
        </DialogHeader>

        <div className="space-y-3">
          <p className="text-[11px] text-cyan-700 bg-cyan-50 border border-cyan-100 rounded px-2 py-1" data-testid="tsedit-tz-hint">
            Times shown &amp; entered in <strong>{timezoneLabel(evtTz)}</strong> (the event timezone).
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="tsedit-ci" className="text-[11px] uppercase tracking-wider text-gray-500">
                Clock In
              </Label>
              <Input
                id="tsedit-ci"
                type="datetime-local"
                value={clockIn}
                onChange={(e) => setClockIn(e.target.value)}
                data-testid="tsedit-clock-in-input"
              />
              {row.original_clock_in && (
                <p className="text-[10px] text-gray-400 mt-1">Original: {row.original_clock_in}</p>
              )}
            </div>
            <div>
              <Label htmlFor="tsedit-co" className="text-[11px] uppercase tracking-wider text-gray-500">
                Clock Out
              </Label>
              <Input
                id="tsedit-co"
                type="datetime-local"
                value={clockOut}
                onChange={(e) => setClockOut(e.target.value)}
                data-testid="tsedit-clock-out-input"
              />
              {row.original_clock_out && (
                <p className="text-[10px] text-gray-400 mt-1">Original: {row.original_clock_out}</p>
              )}
            </div>
          </div>

          {/* Live preview */}
          <div className="rounded-md border border-gray-200 bg-gray-50 p-2 text-[11px] text-gray-700" data-testid="tsedit-preview">
            {preview
              ? preview.valid
                ? (
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <span><strong>{preview.hours.toFixed(2)}h</strong> total</span>
                    <span>Reg: {preview.reg.toFixed(2)}h</span>
                    {preview.ot > 0 && <span className="text-purple-700">OT: {preview.ot.toFixed(2)}h</span>}
                    <span className="text-emerald-700">≈ ${preview.pay.toFixed(2)}</span>
                    <span className="text-gray-400">(break {row.break_minutes || 0} min)</span>
                  </div>
                )
                : <span className="text-rose-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Clock-out must be after clock-in.</span>
              : <span className="text-gray-400">Enter both times to preview hours &amp; pay.</span>
            }
          </div>

          <div>
            <Label className="text-[11px] uppercase tracking-wider text-gray-500">Reason category *</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="tsedit-reason-select">
                <SelectValue placeholder="Pick a reason…" />
              </SelectTrigger>
              <SelectContent>
                {REASON_CATEGORIES.map((r) => (
                  <SelectItem key={r.value} value={r.value} data-testid={`tsedit-reason-${r.value}`}>
                    {r.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="tsedit-note" className="text-[11px] uppercase tracking-wider text-gray-500">
              Notes (optional)
            </Label>
            <Textarea
              id="tsedit-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Any extra context that will show in the audit log."
              data-testid="tsedit-note-input"
            />
          </div>

          <p className="text-[10px] text-gray-500 italic">
            All edits are logged with your name, timestamp, and reason. The talent will be notified.
          </p>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving} data-testid="tsedit-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={saving || !anyChange || !category || !preview?.valid}
            className="bg-cyan-600 hover:bg-cyan-700 text-white"
            data-testid="tsedit-save-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
